import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Coins, ArrowRight, AlertCircle, ShoppingCart } from 'lucide-react';

export default function CreditConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  operationName,
  operationCost = 0.5,
  currentCredits = 0,
  icon: Icon
}) {
  const navigate = useNavigate();
  const remainingCredits = currentCredits - operationCost;
  const hasEnoughCredits = remainingCredits >= 0;

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-sm sm:max-w-md">
        <AlertDialogHeader className="space-y-3">
          <AlertDialogTitle className="flex items-center gap-2 text-lg">
            {Icon && <Icon className="h-5 w-5 text-primary flex-shrink-0" />}
            <span>Confirm {operationName}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                This operation will use minutes from your account.
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Current Balance</span>
                  <span className="font-medium flex items-center gap-1.5 whitespace-nowrap">
                    <Coins className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    {currentCredits.toFixed(1)} min
                  </span>
                </div>
                
                <div className="flex items-center justify-between gap-4 text-red-500">
                  <span className="text-sm">Operation Cost</span>
                  <span className="font-medium whitespace-nowrap">- {operationCost.toFixed(1)} min</span>
                </div>
                
                <div className="border-t border-border pt-2 mt-2 flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Remaining After</span>
                  <span className={`font-semibold flex items-center gap-1.5 whitespace-nowrap ${hasEnoughCredits ? 'text-green-500' : 'text-red-500'}`}>
                    <Coins className="h-4 w-4 flex-shrink-0" />
                    {remainingCredits.toFixed(1)} min
                  </span>
                </div>
              </div>

              {!hasEnoughCredits && (
                <div className="flex gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm space-y-1 min-w-0">
                    <p className="font-medium text-red-500">Insufficient Minutes</p>
                    <p className="text-red-400 leading-relaxed break-words">
                      You need {operationCost.toFixed(1)} min but only have {currentCredits.toFixed(1)} min. Purchase more minutes to continue.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:gap-2 pt-2">
          {!hasEnoughCredits && (
            <Button 
              onClick={() => {
                onClose();
                navigate('/dashboard?buy=true');
              }}
              className="w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
            >
              <ShoppingCart className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Buy More Minutes</span>
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto">
            <AlertDialogCancel className="flex-1 sm:flex-none">Cancel</AlertDialogCancel>
            <Button 
              onClick={() => {
                if (hasEnoughCredits) {
                  onConfirm();
                  onClose();
                }
              }}
              disabled={!hasEnoughCredits}
              className="flex-1 sm:flex-none"
            >
              <ArrowRight className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Confirm & Continue</span>
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
